// src/pages/Moderation/ChannelsViolationsPage/model/useChannelsViolations.ts
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ChannelsCounters, ChannelsViolationsFilter, ModerationChannel } from './types';
import { buildRestrictionAggChannels } from './utils';

type OrderByField = 'title';
export type SortState = { asc: boolean };

function useDebounce<T>(v: T, delay = 400): T {
  const [d, setD] = useState(v);
  useEffect(() => {
    const id = setTimeout(() => setD(v), delay);
    return () => clearTimeout(id);
  }, [v, delay]);
  return d;
}

// ✅ TODO: заміни імпорти на реальні (як у Users)
import {
  getModerationChannelsRequest,
  getModerationFlagsRequest,
} from 'redux/reducers/moderation/reducer';
import {
  getModerationChannelsSelector,
  getModerationFlagsSelector,
  getModerationReportsSelector,
} from 'redux/reducers/moderation/selectors';

export function useChannelsViolations() {
  const dispatch = useDispatch();
  const channelsResp: any = useSelector(getModerationChannelsSelector);
  const flags = useSelector(getModerationFlagsSelector);
  const reports = useSelector(getModerationReportsSelector);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  const [filters, setFilters] = useState<ChannelsViolationsFilter>({
    search: '',
    onlyWithRestrictions: true,
    restriction: undefined,
    dateFrom: undefined,
    dateTo: undefined,
    status: undefined,
  });

  const [sort, setSort] = useState<SortState>({ asc: false });

  const debouncedFilters = useDebounce(filters, 400);

  const channels: ModerationChannel[] = useMemo(() => {
    const d = channelsResp?.data;
    if (!d) return [];
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.channels)) return d.channels;
    return [];
  }, [channelsResp]);

  const total: number = useMemo(() => {
    return Number(channelsResp?.data?.total);
  }, [channelsResp, channels]);

  const counters: ChannelsCounters | null = useMemo(() => {
    const d = channelsResp?.data;
    return (d?.channels_counters ?? null) as ChannelsCounters | null;
  }, [channelsResp]);

  const topRestrictions = useMemo(
    () => buildRestrictionAggChannels(channels).slice(0, 8),
    [channels],
  );

  const restrictionsOptions = useMemo(() => {
    return topRestrictions.map((x) => ({
      id: String(x.restriction),
      label: `Restriction ${x.restriction} · ${x.cnt}`,
    }));
  }, [topRestrictions]);

  const payload = useMemo(() => {
    const p: any = {
      current_page: page,
      per_page: perPage,
      asc: sort.asc,
    };

    if (debouncedFilters.search) p.search = debouncedFilters.search;
    if (debouncedFilters.dateFrom) p.date_from = debouncedFilters.dateFrom;
    if (debouncedFilters.dateTo) p.date_to = debouncedFilters.dateTo;
    if (debouncedFilters.status) p.status = debouncedFilters.status;

    if (typeof debouncedFilters.flag === 'number') p.flag = debouncedFilters.flag;
    if (typeof debouncedFilters.restriction === 'number')
      p.restriction = debouncedFilters.restriction;

    return p;
  }, [page, perPage, sort, debouncedFilters]);

  useEffect(() => {
    dispatch(getModerationChannelsRequest(payload));
    dispatch(getModerationFlagsRequest());
  }, [dispatch, payload]);

  useEffect(() => {
    setPage(1);
  }, [debouncedFilters, sort, perPage]);

  return {
    channels,
    total,
    counters,
    topRestrictions,
    restrictionsOptions,
    page,
    perPage,
    filters,
    sort,
    setPage,
    setPerPage,
    setFilters,
    setSort,
    flags,
  };
}
